import React from "react";
import { Link } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Item from "./Item";
import { cn } from "../../utils/cn";
import useHorizontalScroller from "../../hooks/useHorizontalScroller";

import * as API from '../../../api';

/**
 * Horizontal-scroll strip of gallery cards (used as "Autre Albums" on the
 * gallery detail page). Same premium carousel pattern as Home > Categories
 * and Home > EventTypeCarousel: snap-scroll on mobile, chevron buttons on
 * desktop, mobile progress indicator.
 */

function ScrollButton({ direction, onClick, disabled }) {
  const Icon = direction === 'left' ? ChevronLeftIcon : ChevronRightIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
      className={cn(
        'hidden h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-ink shadow-card transition-all duration-200 lg:flex',
        'hover:border-brand-500 hover:text-brand-500 hover:shadow-card-hover',
        disabled && 'cursor-not-allowed opacity-40 hover:border-slate-200 hover:text-ink hover:shadow-card'
      )}
    >
      <Icon className="h-5 w-5 rtl:rotate-180" />
    </button>
  );
}

const GalleryCard = (props) => {
  const [ galleries        , setGalleries        ] = React.useState([]);
  const [ loadingGalleries , setLoadingGalleries ] = React.useState(false);
  const { scrollerRef, canPrev, canNext, progress, scrollByDir } =
    useHorizontalScroller([galleries.length, loadingGalleries]);

  React.useEffect(()=>{
    setLoadingGalleries(true);
    API.Galleries()
      .then((res)=>{
        setGalleries( res.data );
      })
      .catch((err)=>{
        console.log(err);
      })
      .then(()=>{
        setLoadingGalleries(false);
      })
  },[0]);

  return (
    <section className="relative bg-surface py-8 sm:py-12">
      <div className="mx-auto w-full max-w-container px-4 sm:px-6 lg:px-8">
        { props.titleSection &&
          <div className="flex items-center justify-between gap-4">
            <h2 className="font-display text-[19px] font-semibold tracking-tight text-ink sm:text-[28px] lg:text-3xl">
              {props.titleSection}
            </h2>
            <div className="flex items-center gap-2">
              <ScrollButton direction="left" onClick={() => scrollByDir('left')} disabled={!canPrev} />
              <ScrollButton direction="right" onClick={() => scrollByDir('right')} disabled={!canNext} />
            </div>
          </div>
        }

        <div className="relative mt-3 sm:mt-6">
          <div
            ref={scrollerRef}
            className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 sm:gap-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {
              loadingGalleries ?
              Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[220px] w-[220px] shrink-0 snap-start animate-pulse rounded-2xl bg-slate-100 sm:h-[280px] sm:w-[280px]"
                />
              ))
              :
              galleries.map((item, index) => (
                <Link
                  key={item.id ?? index}
                  to={`/gallery/${item.id}`}
                  className="w-[220px] shrink-0 snap-start sm:w-[280px]"
                >
                  <Item data={item} />
                </Link>
              ))
            }
          </div>
        </div>

        {/* Mobile scroll progress indicator */}
        {!loadingGalleries && galleries.length > 0 && (
          <div className="mt-3 flex items-center justify-center gap-3 sm:mt-4 lg:hidden">
            <div className="relative h-1 w-32 overflow-hidden rounded-full bg-slate-200">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-brand-500 transition-[width] duration-150 rtl:left-auto rtl:right-0"
                style={{ width: `${Math.max(15, progress * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
export default GalleryCard;
